import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { storyElement } from "@/lib/story-element";
import { cn } from "@/lib/utils";
import { Heart, Droplets, Zap, Skull, Star, Shield, Flame, Snowflake, Eye, Camera, RefreshCw, User } from "lucide-react";
import { ImageGenerationDialog } from "@/components/world/ImageGenerationDialog";
import { useLocale } from "@/hooks/useLocale";

interface CharacterSheetProps {
  playerChar: {
    name?: string;
    description?: string;
    stats?: Record<string, any>;
    inventory?: any[];
    conditions?: string[];
    image_url?: string;
  };
  worldId?: string;
  adventureId?: string;
  allowMediaGeneration?: boolean;
  onPortraitChange?: (url: string) => void;
  entityImageMap?: Record<string, string>;
  activeMechanicIds?: Set<string>;
}

const VITAL_BARS: Record<string, { icon: typeof Heart; colorClass: string; max: number }> = {
  hp: { icon: Heart, colorClass: "from-destructive to-destructive/70", max: 100 },
  health: { icon: Heart, colorClass: "from-destructive to-destructive/70", max: 100 },
  mana: { icon: Droplets, colorClass: "from-secondary to-accent", max: 100 },
  mp: { icon: Droplets, colorClass: "from-secondary to-accent", max: 100 },
  stamina: { icon: Zap, colorClass: "from-primary to-primary/60", max: 100 },
  sp: { icon: Zap, colorClass: "from-primary to-primary/60", max: 100 },
};

const CONDITION_ICONS: Record<string, typeof Skull> = {
  poisoned: Skull,
  blessed: Star,
  shielded: Shield,
  burning: Flame,
  frozen: Snowflake,
  invisible: Eye,
};

export function CharacterSheet({ playerChar, worldId, adventureId, allowMediaGeneration = false, onPortraitChange, entityImageMap, activeMechanicIds }: CharacterSheetProps) {
  const { t } = useLocale();
  const resolvedImageUrl = playerChar?.image_url || (entityImageMap && playerChar?.name ? entityImageMap[playerChar.name.toLowerCase().trim()] : null) || null;
  const [portraitUrl, setPortraitUrl] = useState(resolvedImageUrl);
  const [showImageDialog, setShowImageDialog] = useState(false);

  useEffect(() => {
    if (resolvedImageUrl && !portraitUrl) {
      setPortraitUrl(resolvedImageUrl);
    }
  }, [resolvedImageUrl]);

  if (!playerChar?.name) return null;

  const stats = playerChar.stats || {};
  const conditions = playerChar.conditions || [];

  const vitalKeys = Object.keys(stats).filter(k => VITAL_BARS[k.toLowerCase()]);
  const otherStats = Object.entries(stats).filter(([k]) => !VITAL_BARS[k.toLowerCase()] && !k.includes("max_") && !k.includes("_max"));

  const handlePortraitGenerated = (_entityId: string, imageUrl: string) => {
    setPortraitUrl(imageUrl);
    onPortraitChange?.(imageUrl);
  };

  return (
    <div {...storyElement("character-sheet")} className="flex flex-col h-full overflow-hidden">
      <div className="flex items-start gap-3 p-4 pb-3 shrink-0">
        <div className="w-[40%] shrink-0">
          {portraitUrl ? (
            <div className="relative group">
              <img src={portraitUrl} alt={playerChar.name} className="w-full aspect-square object-cover rounded-lg border border-border" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              {allowMediaGeneration && worldId && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <Button variant="secondary" size="sm" className="text-xs gap-1" onClick={() => setShowImageDialog(true)}>
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ) : allowMediaGeneration && worldId ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full aspect-square text-xs gap-1.5 border-dashed flex flex-col"
              onClick={() => setShowImageDialog(true)}
            >
              <Camera className="w-4 h-4" />
              <span className="text-[9px]">{t.play_portrait}</span>
            </Button>
          ) : (
            <div className="w-full aspect-square rounded-lg border border-border bg-muted/20 flex items-center justify-center">
              <User className="w-8 h-8 text-muted-foreground/30" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 py-1 space-y-1.5">
          <h4 className="text-sm font-bold text-foreground tracking-tight leading-tight">{playerChar.name}</h4>
          {playerChar.description && (
            <p className="text-[11px] text-muted-foreground italic leading-relaxed line-clamp-4">{playerChar.description}</p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-5 min-h-0">
      {vitalKeys.length > 0 && (
        <div className="space-y-3">
          {vitalKeys.map(key => {
            const cfg = VITAL_BARS[key.toLowerCase()];
            const value = Number(stats[key]) || 0;
            const max = Number(stats[`${key}_max`] || stats[`max_${key}`]) || cfg.max;
            const percent = Math.min(100, Math.max(0, (value / max) * 100));
            const Icon = cfg.icon;
            const isLow = percent < 25;
            return (
              <div key={key} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Icon className={cn("w-3.5 h-3.5", isLow ? "text-destructive animate-pulse" : "text-muted-foreground")} />
                    <span className="text-xs font-medium text-foreground capitalize">{key}</span>
                  </div>
                  <span className={cn("text-xs font-bold tabular-nums", isLow ? "text-destructive" : "text-foreground")}>
                    {value}<span className="text-muted-foreground font-normal">/{max}</span>
                  </span>
                </div>
                <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-muted/50 border border-border/50">
                  <div
                    className={cn("h-full transition-all duration-700 ease-out rounded-full bg-gradient-to-r", cfg.colorClass, isLow && "animate-pulse")}
                    style={{ width: `${percent}%` }}
                  />
                  <div className="absolute inset-0 flex">
                    {[25, 50, 75].map(tick => (
                      <div key={tick} className="absolute h-full w-px bg-background/20" style={{ left: `${tick}%` }} />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {otherStats.length > 0 && (!activeMechanicIds || activeMechanicIds.has("skill_checks")) && (
        <div className="grid grid-cols-2 gap-1.5">
          {otherStats.map(([key, val]) => (
            <div key={key} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-md bg-muted/20 border border-border/30">
              <span className="text-muted-foreground capitalize truncate">{key.replace(/_/g, " ")}</span>
              <span className="text-foreground font-bold tabular-nums ml-1">{String(val)}</span>
            </div>
          ))}
        </div>
      )}

      {conditions.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">{t.play_conditions}</div>
          <div className="flex flex-wrap gap-1">
            {conditions.map(c => {
              const Icon = CONDITION_ICONS[c.toLowerCase()] || Skull;
              return (
                <Badge key={c} variant="destructive" className="text-[10px] gap-1 animate-fade-in">
                  <Icon className="w-2.5 h-2.5" />{c}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
      </div>

      {allowMediaGeneration && worldId && playerChar.name && (
        <ImageGenerationDialog
          open={showImageDialog}
          onOpenChange={setShowImageDialog}
          entity={{
            id: adventureId || "character",
            name: playerChar.name,
            type: "player_character",
            description: playerChar.description || "",
            world_id: worldId,
          }}
          worldId={worldId}
          onImageGenerated={handlePortraitGenerated}
        />
      )}
    </div>
  );
}
